import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { InvoiceStateContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ShareEntityButton, { ShareEntityButtonStyles } from "@insite/content-library/Components/ShareEntityButton";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import OverflowMenu, { OverflowMenuProps } from "@insite/mobius/OverflowMenu/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { isMgbwInvoiceBrand } from "@tSurya/Common/Utilities/websiteUtils";
import generatePdf from "@tSurya/Handlers/GeneratePdfHandler";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design.surya";
import { THEME_IDS, isThemeActive } from "@tSurya/theme/themeHelper";
import React, { FC, useContext, useState } from "react";
import QuestionCircle from "@insite/mobius/Icons/QuestionCircle";
import ProductAvailabilityInfoIcon from "@tSurya/Icons/ProductAvailabilityInfoIcon";
import siteMessage from "@insite/client-framework/SiteMessage";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    generateEmailAttachmentFromWebpage = "generateEmailAttachmentFromWebpage",
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        rmaLink: getPageLinkByPageType(state, "RequestRmaPage"),
    };
};

const mapDispatchToProps = {
    generatePdf,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface InvoiceDetailHeaderStyles {
    shareEntityButtonStyles?: ShareEntityButtonStyles;
    buttonsHiddenContainer?: HiddenProps;
    menuHiddenContainer?: HiddenProps;
    headerGridContainer?: GridContainerProps;
    narrowOverflowMenu?: OverflowMenuProps;
    title: TypographyProps;
    titleGridItem: GridItemProps;
    printButton?: ButtonPresentationProps;
    returnButton?: ButtonPresentationProps;
    returnRequest?: InjectableCss;
    buttonGridItem?: GridItemProps;
    printClickable?: ClickableProps;
    emailClickable?: ClickableProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    buttonWrapper?: InjectableCss;
    mobileCenteringWrapper?: InjectableCss;
    mobileSpinner?: LoadingSpinnerProps;
    circleIcon?: InjectableCss;
    disableTooltipMessage?: InjectableCss;
    disabledMenuClickable?: ClickableProps;
}

export const headerStyles: InvoiceDetailHeaderStyles = {
    shareEntityButtonStyles: {
        button: {
            buttonType: "outline",
            variant: "secondary",
        },
    },
    printButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            margin-left: 10px;
            padding: 10px 34px !important;
            background-color: transparent !important;
            border: 1px solid ${COLORS.primary.base} !important;
            color: ${COLORS.primary.base} !important;
            > span {
                color: ${COLORS.primary.base} !important;
                line-height: ${TYPOGRAPHY.lineHeight.base};
            }
            &:hover {
                background-color: ${COLORS.primary.base} !important;
                > span {
                    color: ${COLORS.primary.contrast} !important;
                }
            }
        `,
    },
    buttonGridItem: {
        css: css`
            justify-content: flex-end;
        `,
        width: [1, 1, 1, 6, 6],
    },
    titleGridItem: {
        width: [11, 11, 11, 6, 6],
    },
    title: {
        variant: "h2",
        as: "h1",
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.title};
            font-weight: ${TYPOGRAPHY.weight.bold};
            font-size: ${TYPOGRAPHY.size.larger};
            line-height: ${TYPOGRAPHY.lineHeight.lmedium};
            font-style: normal;
        `,
    },
    buttonsHiddenContainer: {
        below: "lg",
    },
    menuHiddenContainer: {
        above: "md",
    },
    returnButton: {
        css: css`
            margin-left: 10px;
            padding: 10px 34px !important;
            background-color: ${COLORS.primary.base} !important;
            border-color: ${COLORS.primary.base} !important;
            border: 2px solid;
            > span {
                line-height: ${TYPOGRAPHY.lineHeight.base};
            }

            &:not(:disabled) > span {
                color: ${COLORS.primary.contrast} !important;
            }

            &:not(:disabled):hover {
                background-color: ${COLORS.text.accent} !important;
                span[class^="TypographyStyle"] {
                    color: ${COLORS.primary.contrast} !important;
                }
            }

            /* Surya and Global Views have no themed disabled button style, so the MGBW
               grays are applied here to keep the blocked Return button identical everywhere. */
            &:disabled {
                background-color: #cccccc !important;
                border-color: transparent !important;
                color: #4d4d4d !important;
                cursor: not-allowed;
                opacity: 1;
                > span,
                span[class^="TypographyStyle"] {
                    color: #4d4d4d !important;
                }
            }
        `,
    },
    returnRequest: {
        css: css`
            display: inline-flex;
            align-items: center;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 40px;
            width: 100%;
            display: flex;
            align-items: center;
            padding: 0px 30px;
        `,
    },
    spinner: {
        size: 30,
        css: css`
            margin: auto;
        `,
    },
    buttonWrapper: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    mobileCenteringWrapper: {
        css: css`
            padding: 10px 15px;
        `,
    },
    mobileSpinner: {
        size: 24,
        css: css`
            margin: auto;
        `,
    },
    printClickable: {
        css: css`
            font-size: ${TYPOGRAPHY.button.size};
            font-weight: ${TYPOGRAPHY.button.weight};
            font-family: ${TYPOGRAPHY.button.fontFamily};
            line-height: ${TYPOGRAPHY.button.lineHeight};

            span {
                font-size: ${TYPOGRAPHY.button.size};
                font-weight: ${TYPOGRAPHY.button.weight};
                font-family: ${TYPOGRAPHY.button.fontFamily};
                line-height: ${TYPOGRAPHY.button.lineHeight};
            }
        `,
    },
    disabledMenuClickable: {
        css: css`
            font-size: ${TYPOGRAPHY.button.size};
            font-weight: ${TYPOGRAPHY.button.weight};
            font-family: ${TYPOGRAPHY.button.fontFamily};
            line-height: ${TYPOGRAPHY.button.lineHeight};

            &:disabled {
                color: ${COLORS.text.disabled} !important;
                cursor: not-allowed;
                background: transparent !important;
            }
        `,
    },
    circleIcon: {
        css: css`
            position: relative;
            display: inline-flex;
            top: 2px;
            margin-left: 2px;
            color: inherit;
            align-items: center;
            > span {
                display: none;
            }
            &:hover > span {
                display: inline-block;
            }
        `,
    },
    disableTooltipMessage: {
        css: css`
            display: none;
            position: absolute;
            right: 13px;
            background: #323132;
            padding: 5px 10px;
            border-radius: 4px;
            z-index: 99;
            font-size: 12px !important;
            color: white;
            font-weight: 600 !important;
            top: -40px;
            white-space: nowrap !important;
        `,
    },
};

const styles = headerStyles;
const InvoiceDetailHeader: FC<Props> = ({ fields, rmaLink, generatePdf }) => {
    const { value: invoice } = useContext(InvoiceStateContext);
    const toasterContext = React.useContext(ToasterContext);
    const [isLoading, setIsLoading] = useState(false);
    const resolvedGenerateEmailAttachmentFromWebpage =
        fields.generateEmailAttachmentFromWebpage === true || fields.generateEmailAttachmentFromWebpage === undefined;
    if (!invoice) {
        return null;
    }

    const onClickGeneratePdf = () => {
        setIsLoading(true);
        generatePdf({
            identifier: invoice.invoiceNumber,
            templateName: "invoice",
            onSuccess(result) {
                setIsLoading(false);
            },
            onError(result) {
                setIsLoading(false);
                toasterContext.addToast({
                    body: translate(`${result}`),
                    messageType: "danger",
                });
            },
        });
    };
    const orderNo = invoice.properties.erpOrderNo;
    const isMgbw = isThemeActive(THEME_IDS.MGBW);
    const isReturnButtonDisabled =
        isMgbwInvoiceBrand(invoice.properties?.brand, isMgbw) &&
        invoice.properties?.isReturnSelfService?.toLowerCase() !== "true";

    const printLabel = translate("Print");

    return (
        <GridContainer {...styles.headerGridContainer}>
            <GridItem {...styles.titleGridItem}>
                <Typography {...styles.title}>{`${translate("Invoice")} # ${invoice.invoiceNumber}`}</Typography>
            </GridItem>
            <GridItem {...styles.buttonGridItem}>
                <Hidden {...styles.menuHiddenContainer}>
                    {
                        !isLoading &&(

                            <OverflowMenu hasChildPortal position="end" {...styles.narrowOverflowMenu}>
                            {/* commenting these lines because we not trigger Email as a phase 1 of project */}
    
                            {/* <ShareEntityButton
                                entityId={invoice.invoiceNumber}
                                variant="clickable"
                                entityName="Invoice"
                                extendedStyles={styles.shareEntityButtonStyles}
                                generateAttachmentFromWebpage={resolvedGenerateEmailAttachmentFromWebpage}
                            /> */}
                                {!isLoading ? (
                                    <Clickable
                                        {...styles.printClickable}
                                        onClick={onClickGeneratePdf}
                                        data-test-selector="invoiceDetails_print"
                                        disabled={isLoading}
                                    >
                                        {printLabel}
                                    </Clickable>
                                ) : (
                                    <StyledWrapper {...styles.mobileCenteringWrapper}>
                                        <LoadingSpinner {...styles.mobileSpinner} />
                                    </StyledWrapper>
                                )}
                                {invoice?.invoiceType === "IN" && orderNo && (
                                    isReturnButtonDisabled ? (
                                        // as="button" so the element is natively disabled; Clickable renders a span without href/onClick
                                        <Clickable
                                            {...styles.disabledMenuClickable}
                                            as="button"
                                            type="button"
                                            disabled
                                            title={siteMessage("MGBW_returns_contactSupport").toString()}
                                            data-test-selector="invoiceDetails_return"
                                        >
                                            {translate("Return")}
                                        </Clickable>
                                    ) : (
                                        <Clickable
                                            {...styles.printClickable}
                                            href={`${rmaLink?.url}?orderNumber=${orderNo}&invoiceNumber=${invoice.invoiceNumber}&invoiceDate=${invoice.invoiceDate}&PoNumber=${invoice.customerPO}`}
                                            data-test-selector="invoiceDetails_return"
                                        >
                                            {translate("Return")}
                                        </Clickable>
                                    )
                                )}
                            </OverflowMenu>
                        )
                    }
                   
                </Hidden>
                <Hidden {...styles.buttonsHiddenContainer}>
                    <StyledWrapper {...styles.buttonWrapper}>
                        {/* <ShareEntityButton
                        entityId={invoice.invoiceNumber}
                        entityName="Invoice"
                        extendedStyles={styles.shareEntityButtonStyles}
                        generateAttachmentFromWebpage={resolvedGenerateEmailAttachmentFromWebpage}
                    /> */}
                        {!isLoading &&(
                            <Button
                                {...styles.printButton}
                                onClick={onClickGeneratePdf}
                                data-test-selector="invoiceDetails_print"
                                disabled={isLoading}
                            >
                                {printLabel}
                            </Button>
                        )}
                        {isLoading && (
                            <StyledWrapper {...styles.centeringWrapper}>
                                <LoadingSpinner {...styles.spinner} />
                            </StyledWrapper>
                        )}
                        {orderNo && invoice?.invoiceType === "IN" ? (
                            isReturnButtonDisabled ? (
                                <div style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                                    <Clickable
                                        {...styles.returnRequest}
                                    >
                                        <Button {...styles.returnButton} variant="primary" disabled>
                                            <span style={{ verticalAlign: "top" }}>Return</span>
                                            <Typography as="div" {...styles.circleIcon}>
                                                <QuestionCircle size={16} src={ProductAvailabilityInfoIcon} />{" "}
                                                <StyledWrapper as="span" {...styles.disableTooltipMessage}>
                                                    {siteMessage("MGBW_returns_contactSupport").toString()}
                                                </StyledWrapper>{" "}
                                            </Typography>
                                        </Button>
                                    </Clickable>
                                </div>
                            ) : (
                                <Clickable
                                    {...styles.returnRequest}
                                    href={`${rmaLink?.url}?orderNumber=${orderNo}&invoiceNumber=${invoice.invoiceNumber}&invoiceDate=${invoice.invoiceDate}&PoNumber=${invoice.customerPO}`}
                                >
                                    <Button {...styles.returnButton} variant="primary">
                                        Return
                                    </Button>
                                </Clickable>
                            )
                        ) : null}
                    </StyledWrapper>
                </Hidden>
            </GridItem>
        </GridContainer>
    );
};
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(InvoiceDetailHeader)),
    definition: {
        displayName: "Page Header",
        allowedContexts: ["InvoiceDetailsPage"],
        group: "Invoice Details",
        fieldDefinitions: [
            {
                name: fields.generateEmailAttachmentFromWebpage,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tooltip:
                    "If checked, sharing the invoice generates an email attachment using the Invoice Details page, rather than a pre-defined PDF file.",
            },
        ],
    },
};
export default widgetModule;
